import { io, Socket } from 'socket.io-client';
import store from '../store';
import { messagesActions } from '../store/messages';
import { Message } from '../models/data';

const SOCKET_URL = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001')
  : window.location.origin;

class SocketService {
  private socket: Socket | null = null;
  private isConnecting: boolean = false;

  /**
   * Connect to Socket.io server with JWT authentication
   */
  connect(token: string) {
    if (this.socket?.connected) {
      console.log('[Socket Service] Already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('[Socket Service] Connection already in progress');
      return;
    }

    this.isConnecting = true;

    console.log('[Socket Service] Connecting to', SOCKET_URL);

    this.socket = io(SOCKET_URL, {
      auth: {
        token,
      },
      transports: ['websocket', 'polling'],
    });

    this.setupEventListeners();
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect() {
    if (this.socket) {
      console.log('[Socket Service] Disconnecting');
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      store.dispatch(messagesActions.setConnected({ isConnected: false }));
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Setup Socket.io event listeners
   */
  private setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('[Socket Service] Connected');
      this.isConnecting = false;
      store.dispatch(messagesActions.setConnected({ isConnected: true }));
    });

    this.socket.on('disconnect', () => {
      console.log('[Socket Service] Disconnected');
      store.dispatch(messagesActions.setConnected({ isConnected: false }));
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket Service] Connection error:', error);
      this.isConnecting = false;
      store.dispatch(messagesActions.setConnected({ isConnected: false }));
    });

    // Message events
    this.socket.on('message:new', ({ message, conversationId }: { message: Message; conversationId: string }) => {
      console.log('[Socket Service] New message received:', message.id);
      store.dispatch(messagesActions.addMessage({ message }));
    });

    this.socket.on('message:notification', ({ message, conversationId, senderId }: { message: Message; conversationId: string; senderId: string }) => {
      console.log('[Socket Service] Message notification:', conversationId);

      // Add message to state
      store.dispatch(messagesActions.addMessage({ message }));

      // Increment unread count if not currently in this conversation
      const state = store.getState();
      const currentConversation = state.messages.currentConversation;

      if (!currentConversation || currentConversation.id !== conversationId) {
        store.dispatch(messagesActions.incrementUnreadCount({ conversationId }));
      }
    });

    // Typing events
    this.socket.on('typing:start', ({ conversationId, userId }: { conversationId: string; userId: string }) => {
      console.log('[Socket Service] User typing:', userId);
      store.dispatch(messagesActions.setTyping({ conversationId, isTyping: true }));
    });

    this.socket.on('typing:stop', ({ conversationId, userId }: { conversationId: string; userId: string }) => {
      console.log('[Socket Service] User stopped typing:', userId);
      store.dispatch(messagesActions.setTyping({ conversationId, isTyping: false }));
    });

    // Read receipts
    this.socket.on('messages:read', ({ conversationId, messageId, userId }: { conversationId: string; messageId: string; userId: string }) => {
      console.log('[Socket Service] Messages read by user:', userId);
      // Could add read receipt UI here in the future
    });

    // Error events
    this.socket.on('error', ({ message }: { message: string }) => {
      console.error('[Socket Service] Server error:', message);
    });
  }

  /**
   * Join a conversation room
   */
  joinConversation(conversationId: string) {
    if (!this.socket?.connected) {
      console.warn('[Socket Service] Cannot join conversation - not connected');
      return;
    }

    console.log('[Socket Service] Joining conversation:', conversationId);
    this.socket.emit('conversation:join', { conversationId });
  }

  /**
   * Leave a conversation room
   */
  leaveConversation(conversationId: string) {
    if (!this.socket?.connected) {
      console.warn('[Socket Service] Cannot leave conversation - not connected');
      return;
    }

    console.log('[Socket Service] Leaving conversation:', conversationId);
    this.socket.emit('conversation:leave', { conversationId });
  }

  /**
   * Send a message
   */
  sendMessage(conversationId: string, content: string) {
    if (!this.socket?.connected) {
      console.warn('[Socket Service] Cannot send message - not connected');
      return false;
    }

    console.log('[Socket Service] Sending message to:', conversationId);
    this.socket.emit('message:send', { conversationId, content });
    return true;
  }

  /**
   * Start typing indicator
   */
  startTyping(conversationId: string) {
    if (!this.socket?.connected) return;

    this.socket.emit('typing:start', { conversationId });
  }

  /**
   * Stop typing indicator
   */
  stopTyping(conversationId: string) {
    if (!this.socket?.connected) return;

    this.socket.emit('typing:stop', { conversationId });
  }

  /**
   * Mark messages as read
   */
  markAsRead(conversationId: string, messageId: string) {
    if (!this.socket?.connected) return;

    console.log('[Socket Service] Marking messages as read:', conversationId, messageId);
    this.socket.emit('messages:read', { conversationId, messageId });
  }
}

// Export singleton instance
export const socketService = new SocketService();
